import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { TESTID } from "@/lib/testIds";
import { LogOut, LayoutDashboard, Users, Activity, ShieldCheck, Shield, Store, Coins, Heart, Vault } from "lucide-react";

export function ArmadaNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!user) return null;

  const isSovereign = user.role === "sovereign";
  const isJunior = user.role === "junior_admiral" || user.role === "lineage_kin";
  const isGuardian = user.role === "guardian";

  const nav = [
    { to: "/dashboard", label: "Command", icon: LayoutDashboard, testId: TESTID.navDashboard, show: true },
    { to: "/junior-command", label: "Heir Vault", icon: Vault, testId: "nav-junior-link", show: isJunior || isSovereign },
    { to: "/parental-bridge", label: "Bridge", icon: Heart, testId: "nav-bridge-link", show: isGuardian || isSovereign },
    { to: "/roster", label: "Roster", icon: Users, testId: TESTID.navRoster, show: isSovereign },
    { to: "/treasury", label: "Treasury", icon: Coins, testId: "nav-treasury-link", show: isSovereign },
    { to: "/signals", label: "Signals", icon: Activity, testId: TESTID.navSignals, show: isSovereign },
    { to: "/shop", label: "Shop", icon: Store, testId: "nav-shop-link", show: true },
    { to: "/security", label: "Security", icon: ShieldCheck, testId: TESTID.navProfile, show: true },
  ];

  return (
    <nav className="sticky top-0 z-50 glass border-b border-white/5">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
        <Link to="/dashboard" data-testid={TESTID.navLogo} className="flex items-center gap-3 group">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A227] to-[#8C7320] flex items-center justify-center glow-gold">
            <Shield className="w-5 h-5 text-black" strokeWidth={2.5} />
          </div>
          <div className="hidden md:block">
            <div className="text-base font-black tracking-tight text-white">EIGOO Armada</div>
            <div
              data-testid="header-eigoo-badge"
              className="text-[9px] tracking-armada text-[#C9A227] font-bold"
            >
              EMPOWERED BY EIGOO INC
            </div>
          </div>
        </Link>

        <div className="flex items-center gap-1 md:gap-2">
          {nav.filter((n) => n.show).map(({ to, label, icon: Icon, testId }) => {
            const active = location.pathname === to || (to === "/security" && location.pathname === "/profile");
            return (
              <Link
                key={to}
                to={to}
                data-testid={testId}
                className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  active
                    ? "bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30"
                    : "text-zinc-400 hover:text-white border border-transparent hover:bg-white/5"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden md:inline">{label}</span>
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden md:flex flex-col items-end">
            <div className="text-xs tracking-wider text-zinc-500 uppercase">{user.role}</div>
            <div className="text-sm font-semibold text-white">{user.full_name}</div>
          </div>
          <button
            data-testid={TESTID.navLogout}
            onClick={async () => { await logout(); navigate("/login"); }}
            className="p-2 rounded-md text-zinc-400 hover:text-[#D4AF37] hover:bg-white/5 transition-colors"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </nav>
  );
}
